import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getOrcamentos,
  getAutorizacoesPendentesCount,
  vincularPendentes,
  getMeuVinculoNasajon,
} from '../../../api/orcamentoCiliaApi';
import { queryKeys } from '../../../infrastructure/react-query/queryKeys';
import { notify } from '../../../shared/feedback/notify';
import { useIsTi } from '../../../hooks/useUserType';
import { useAlcanca } from '../../../hooks/usePapeis';
import ImportXmlModal from '../components/ImportXmlModal';
import FiltroRequisicoesModal, {
  FILTROS_REQUISICOES_PADRAO,
  FiltrosDaTela,
  paraParametros,
} from '../components/FiltroRequisicoesModal';
import FilterIcon from '../../../img/filter.svg';
import AutorizacoesTab from '../components/AutorizacoesTab';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import { ORCAMENTOS_CILIA_DETAIL } from '../routes';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';
import SearchIcon from '../../../img/search-icon.svg';
import '../../recepcao/pages/Recepcao.css';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/ui/badge-status.css';
import '../../../shared/feedback/aviso-de-tela.css';
import './OrcamentoCiliaList.css';
// botões link-action do painel de avisos (reaproveitados da tela de detalhe)
import './OrcamentoCiliaDetail.css';


// Na tabela o veículo aparece só pelo modelo ("VIRTUS"): o nome do Cilia traz geração,
// versão e motor, que cortavam a célula. O nome inteiro fica no `title`.
const nomeCurtoDoVeiculo = (nome?: string | null): string => {
  if (!nome) return '—';
  const semGeracao = nome.split('(')[0].trim();
  return semGeracao.split(/\s+/).slice(0, 3).join(' ') || nome;
};

// Primeiro e último nome; o nome inteiro fica no `title`.
const nomeCurtoDaPessoa = (nome?: string | null): string => {
  if (!nome) return '—';
  const partes = nome.trim().split(/\s+/);
  return partes.length <= 2 ? partes.join(' ') : `${partes[0]} ${partes[partes.length - 1]}`;
};

const OrcamentoCiliaList: React.FC<{ sidebarRef?: React.RefObject<HTMLDivElement> }> = () => {
  const navigate = useNavigate();
  const isTi = useIsTi();
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  // Abas (decisão de 30/07/2026 — sem tela própria de autorizações, padrão
  // "Em atendimento"/"Entregues" do Chefe de Oficina). Visibilidade segue os
  // papéis do backend: a lista é ORCAMENTO/ADMIN/TI; a fila é CHEFE/ADMIN/TI.
  //
  // 🔴 Decide pelo CONJUNTO de papéis (ADR 0044), nunca por `userType ===`: quem tem
  // ORCAMENTO como papel secundário passa pelo `RoleGuard` (que já usa `alcanca`) e via a
  // tela **só com o título** — nenhuma das duas abas renderizava. Aconteceu em produção com
  // `orc.juliana` (principal ADMINISTRACAO + ORCAMENTO), 25/09/2026.
  // COMPRA vê a lista em LEITURA (orcamento-cilia/at-15): é quem decide a compra pela cotação online.
  const podeVerOrcamentos = useAlcanca(['ORCAMENTO', 'COMPRA', 'ADMIN', 'TI']);
  const podeImportar = useAlcanca(['ORCAMENTO']);
  const podeVerAutorizacoes = useAlcanca(['CHEFE_DE_OFICINA', 'ADMIN', 'TI']);
  const [activeTab, setActiveTab] = useState<'ORCAMENTOS' | 'AUTORIZACOES'>(
    podeVerOrcamentos ? 'ORCAMENTOS' : 'AUTORIZACOES',
  );

  // Badge da aba: complementos aguardando o Chefe.
  const { data: autorizacoesCount, isError: erroAutorizacoesCount } = useQuery({
    queryKey: queryKeys.orcamentoCilia.autorizacoesPendentesCount,
    queryFn: getAutorizacoesPendentesCount,
    enabled: podeVerAutorizacoes,
    refetchInterval: 30_000,
  });

  // Polling silencioso do badge (BUG-V01): sem isto, backend do módulo fora do ar
  // ficaria indistinguível de "nenhuma pendência" — mesmo tratamento da Sidebar.
  useEffect(() => {
    if (erroAutorizacoesCount) {
      notify.warning(
        'Não foi possível atualizar a contagem de autorizações pendentes.',
        { toastId: 'orcamento-cilia-autorizacoes-count' },
      );
    }
  }, [erroAutorizacoesCount]);


  // Filtros e paginação
  const [search, setSearch] = useState('');
  // at-18: filtros em modal, estado DA TELA (não global). Aplicar volta à página 1.
  const [filtros, setFiltros] = useState<FiltrosDaTela>(FILTROS_REQUISICOES_PADRAO);
  const [isFiltroOpen, setIsFiltroOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const queryClient = useQueryClient();

  /*
   * at-12: liga ao atendimento da Oficina as requisições que nasceram sem ele. Roda uma vez ao
   * abrir a tela, DEPOIS de a lista começar a carregar e sem segurá-la — o módulo só fala com a
   * Oficina com o login de quem está usando, então é a abertura da tela que dispara o vínculo.
   * O TI não tem a rota (escreve), então não chama.
   */
  const podeVincular = useAlcanca(['ORCAMENTO', 'ADMIN']);
  useEffect(() => {
    if (!podeVincular) return;
    let ativo = true;
    const avisarFalha = () =>
      notify.warning(
        'Não foi possível conferir se as requisições sem atendimento já têm o veículo na Oficina. Elas continuam listadas; o vínculo é tentado de novo na próxima abertura.',
        { toastId: 'orcamento-cilia-vincular-pendentes' },
      );
    vincularPendentes()
      .then((r) => {
        if (!ativo) return;
        if (r.falhou) avisarFalha();
        if (r.vinculados > 0) queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.all });
      })
      .catch(() => {
        if (ativo) avisarFalha();
      });
    return () => {
      ativo = false;
    };
  }, [podeVincular, queryClient]);

  /*
   * orcamento-cilia/at-13: quem conclui orçamento precisa estar vinculado ao seu usuário do
   * Nasajon — a requisição sai em nome dele. O backend tenta vincular pelo e-mail ao responder;
   * se não conseguir, o aviso aparece aqui, na entrada, e não no meio do "Concluir e Enviar".
   * Mesmo público da rota (ORCAMENTO; ADMIN por bypass). O TI não conclui, então não consulta.
   */
  const podeConcluir = useAlcanca(['ORCAMENTO', 'ADMIN']);
  const { data: meuVinculo, isError: erroMeuVinculo } = useQuery({
    queryKey: queryKeys.orcamentoCilia.meuVinculoNasajon,
    queryFn: getMeuVinculoNasajon,
    enabled: podeConcluir,
    staleTime: 5 * 60_000,
  });

  // Query React Query
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: queryKeys.orcamentoCilia.list({ page, limit, search, ...filtros }),
    queryFn: () => getOrcamentos({ page, limit, search, ...paraParametros(filtros) }),
  });

  /*
   * Tempo real (ADR 0003 do módulo). É aceleração, não substituto: as consultas da tela
   * continuam sendo a fonte.
   *
   * ⚠️ Os avisos do módulo NÃO são mais lidos aqui (portal-cotacoes/at-14): vão para o sino do
   * topo, que escuta o mesmo `cilia:notificacao` por conta própria.
   *
   * ⚠️ Quem invalida é a TELA, não o hook global: ele é montado no `App`, que renderiza o
   * `QueryClientProvider`, então `useQueryClient` lá estouraria. O hook publica
   * `CustomEvent`; aqui dentro do provider é que se refaz a consulta.
   *
   * Um aviso pode ser o do 4º complemento (muda a fila do Chefe) ou o desfecho de uma
   * autorização (muda a lista do orçamentista) — invalidar os três é mais barato que
   * decidir pelo tipo, e o react-query só refaz o que está montado.
   */
  useEffect(() => {
    const recarregarTudo = () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.autorizacoesPendentesCount });
      queryClient.invalidateQueries({ queryKey: queryKeys.orcamentoCilia.all });
    };

    window.addEventListener('cilia:notificacao', recarregarTudo);
    window.addEventListener('cilia:orcamento-atualizado', recarregarTudo);
    return () => {
      window.removeEventListener('cilia:notificacao', recarregarTudo);
      window.removeEventListener('cilia:orcamento-atualizado', recarregarTudo);
    };
  }, [queryClient]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleAplicarFiltros = (novos: FiltrosDaTela) => {
    setFiltros(novos);
    setPage(1);
  };

  const getEstadoBadgeClass = (estado: string) => {
    switch (estado) {
      case 'EM_CONFERENCIA': return 'badge-azul';
      case 'ENVIADO': return 'badge-cinza';
      case 'APROVADO': return 'badge-verde';
      case 'REJEITADO': return 'badge-vermelho';
      case 'EM_AJUSTE': return 'badge-laranja';
      case 'AGUARDANDO_AUTORIZACAO': return 'badge-laranja';
      case 'AUTORIZACAO_NEGADA': return 'badge-vermelho';
      default: return 'badge-cinza';
    }
  };

  /*
   * `temRequisicao` (13/08/2026): o estado `ENVIADO` cobre dois desfechos — a conferência
   * fechou criando requisição, ou fechou SEM criar (versão sem peça comprável). Rotular os
   * dois como "Requisição Criada" contradiz o toast que o usuário acabou de ler ("Nenhuma
   * requisição foi criada") e o manda procurar no Portal um card que nunca vai existir.
   */
  const getEstadoLabel = (estado: string, temRequisicao = true) => {
    switch (estado) {
      case 'EM_CONFERENCIA': return 'Em Conferência';
      // Estado é sobre a REQUISIÇÃO (pedido saiu do escopo — decisão 29/07)
      case 'ENVIADO': return temRequisicao ? 'Requisição Criada' : 'Sem Requisição';
      case 'AGUARDANDO_APROVACAO': return 'Aguardando Aprovação';
      case 'APROVADO': return 'Requisição Aprovada';
      case 'REJEITADO': return 'Requisição Reprovada';
      case 'EM_AJUSTE': return 'Em Ajuste';
      case 'AGUARDANDO_AUTORIZACAO': return 'Aguardando Autorização';
      case 'AUTORIZACAO_NEGADA': return 'Autorização Negada';
      default: return estado;
    }
  };

  // Seções de ajuda para o "Como Funciona"
  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: '1. Importação de XML',
      conteudo: (
        <div>
          <p>Clique no botão <strong>Importar XML</strong> para enviar um arquivo do Cilia (apenas formato <code>.xml</code> de até 5MB).</p>
          <p>O arquivo precisa estar no estado <strong>Analisado</strong> no Cilia.</p>
          <p>O orçamento é ligado ao atendimento do veículo na Oficina pela <strong>placa</strong>, conferindo o <strong>evento</strong>. Se a placa ainda não tiver atendimento aberto, o sistema avisa e a requisição pode ser iniciada mesmo assim. Se a placa estiver na Oficina com outro evento, aparece um aviso de atenção: só continue se tiver certeza de que o orçamento não é daquele atendimento.</p>
          <p>A requisição iniciada sem atendimento aparece com o selo <strong>Sem atendimento na Oficina</strong> e é ligada sozinha quando alguém abre esta tela e o veículo já deu entrada: pelo mesmo evento, ou só pela placa quando a Oficina cadastrou o veículo sem evento. Ela nunca é ligada ao atendimento recusado no aviso de evento diferente.</p>
          <p>Para testar o envio ao Nasajon, marque <strong>Requisição de teste</strong> na importação. A API da Nasajon não tem modo de teste: o que marca uma requisição como teste é o <strong>Motivo da Requisição</strong>, e o sistema escreve <strong>"TESTE - NÃO COMPRAR"</strong> na primeira linha dele. A requisição aparece com o selo <strong>Teste</strong>. A marcação vale para aquela importação e não pode ser trocada depois.</p>
        </div>
      )
    },
    {
      titulo: 'Filtros, busca e a coluna Oficina',
      conteudo: (
        <div>
          <p>A busca acha pela <strong>placa</strong>, pelo <strong>nº do Cilia</strong>, pelo <strong>evento</strong> e pelo nome da <strong>oficina</strong>.</p>
          <p>O botão <strong>Filtros</strong> abre as demais opções: estado, tipo (inicial ou complemento), perda total, atendimento na Oficina, requisições de teste, oficina, quem importou e o período de importação. Elas só passam a valer em <strong>Aplicar Filtros</strong>, voltam a lista para a primeira página e se somam à busca. Para conferir ou desfazer o que está aplicado, abra o <strong>Filtros</strong> de novo.</p>
          <p>O período é o dia em que a requisição foi <strong>importada</strong> no sistema, não a data do orçamento no Cilia.</p>
          <p>A coluna <strong>Oficina</strong> mostra a oficina que veio no XML do Cilia. É a mesma que vai no Motivo da Requisição enviado ao Nasajon.</p>
        </div>
      )
    },
    {
      titulo: '2. Triagem e Conferência',
      conteudo: (
        <div>
          <p>Ao selecionar a placa de um orçamento importado, você é levado à tela de conferência.</p>
          <p>Lá você deve preencher a <strong>Esteira</strong> de destino no SOAC e conferir/preencher o <strong>NCM</strong> de cada peça.</p>
        </div>
      )
    },
    {
      titulo: '3. Envio ao Nasajon',
      conteudo: (
        <div>
          <p>Uma vez que todas as peças tenham esteiras e NCM válidos, o botão de conclusão será liberado.</p>
          <p>O sistema criará de forma automática no ERP as peças que ainda não existem lá e a <strong>requisição de compra</strong> com todas as peças a comprar, já em nome de quem conferiu.</p>
          <p>Para isso, sua conta precisa estar <strong>vinculada ao seu usuário do Nasajon</strong>. O vínculo é feito sozinho pelo seu e-mail empresarial, que é o mesmo nos dois sistemas. Quando o e-mail não bate (foi trocado, digitado de outro jeito, ou você ainda não tem usuário no Nasajon), aparece no topo da tela o aviso para informar ao TI, que faz o vínculo à mão.</p>
        </div>
      )
    },
    {
      titulo: '4. Reabertura',
      conteudo: (
        <div>
          <p>Caso o orçamento seja rejeitado no processo manual/assistido, você poderá clicar em <strong>Ajustar e Reenviar</strong> na tela de detalhe para reabri-lo, deixando os itens editáveis novamente.</p>
        </div>
      )
    }
  ];

  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  const startCarIndex = data ? (page - 1) * limit + 1 : 0;
  const endCarIndex = data ? Math.min(page * limit, data.total) : 0;
  const totalCars = data ? data.total : 0;

  return (
    <div className="ConteDo recepcao-conte-do">
      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Requisições
          <button 
            className="help-circle-btn" 
            onClick={() => setIsHelpOpen(true)}
            title="Como Funciona"
          >
            ?
          </button>
        </div>
        
        {activeTab === 'ORCAMENTOS' && (
          <div className="recepcao-search-bar">
            <div className="ItemSearch">
              <img
                className="imgIcon"
                src={SearchIcon}
                alt="Ícone de busca"
                width="20"
                height="20"
              />
            </div>
            <input
              id="cilia-search"
              type="text"
              placeholder="Placa, nº Cilia, evento ou oficina..."
              value={search}
              onChange={handleSearchChange}
              className="recepcao-search-input"
            />
          </div>
        )}
      </div>

      {/*
        at-13: três estados que não podem se parecer — "não vinculada" é cadastro (vai ao TI);
        "não consegui conferir" é queda do Nasajon ou do módulo, e mandar ao TI ali seria errado.
      */}
      {podeConcluir && meuVinculo && !meuVinculo.vinculo && meuVinculo.motivo !== 'NASAJON_INDISPONIVEL' && (
        <div className="aviso-de-tela" role="status">
          <span className="aviso-de-tela-titulo">Sua conta ainda não foi vinculada ao Nasajon, informe ao TI.</span>
          Sem o vínculo, o "Concluir e Enviar" não consegue criar a requisição de compra em seu nome.
        </div>
      )}
      {podeConcluir && (erroMeuVinculo || meuVinculo?.motivo === 'NASAJON_INDISPONIVEL') && (
        <div className="aviso-de-tela" role="status">
          Não foi possível conferir agora se sua conta está vinculada ao Nasajon. Isso é conferido de
          novo no "Concluir e Enviar".
        </div>
      )}

      {/* Abas (padrão do Chefe de Oficina) — só aparecem para quem tem as duas */}
      {podeVerOrcamentos && podeVerAutorizacoes && (
        <div className="recepcao-tabs">
          <button
            type="button"
            className={`recepcao-tab ${activeTab === 'ORCAMENTOS' ? 'active' : ''}`}
            onClick={() => setActiveTab('ORCAMENTOS')}
          >
            Orçamentos
          </button>
          <button
            type="button"
            className={`recepcao-tab ${activeTab === 'AUTORIZACOES' ? 'active' : ''}`}
            onClick={() => setActiveTab('AUTORIZACOES')}
          >
            Autorizações{(autorizacoesCount ?? 0) > 0 ? ` (${autorizacoesCount})` : ''}
          </button>
        </div>
      )}

      {activeTab === 'AUTORIZACOES' && podeVerAutorizacoes && <AutorizacoesTab />}

      {activeTab === 'ORCAMENTOS' && podeVerOrcamentos && (
      <>
      <div className="VisualizarOrdenar">
        <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex', flexWrap: 'wrap', width: '100%' }}>
          
          <div className="CtaBig">
            <div className="Frame1000003987">
              <div className="ComponentName">Itens por página:</div>
              <select
                id="limit"
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                style={{ border: 'none', background: 'transparent', outline: 'none', cursor: 'pointer', fontFamily: 'AmpleSoft-Bold, sans-serif', fontWeight: '400', color: 'var(--tema-texto, #1A1C1B)', fontSize: '15px' }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          {/* at-18: os filtros vivem no modal (padrão do sistema); "Limpar filtros" está dentro dele. */}
          <div className="CtaBig MouseHover" onClick={() => setIsFiltroOpen(true)}>
            <div className="Frame1000003987">
              <img src={FilterIcon} alt="Filtros" width="19.80" height="19.80" style={{ cursor: 'pointer' }} />
              <div className="ComponentName">Filtros</div>
            </div>
          </div>

          {/*
            O botão "Configurações" saiu daqui em 12/08/2026: a configuração do módulo
            vive na Administração do Sistema. Ele também era oferecido a quem não podia
            entrar — o RoleGuard da tela é ADMIN/TI, então o orçamentista que clicasse
            era jogado para a Home sem explicação.
          */}
          <div style={{ display: 'flex', gap: 12, marginLeft: 'auto', alignItems: 'center' }}>
            {!isTi && podeImportar && (
              <div className="CtaSmall MouseHover" onClick={() => setIsImportModalOpen(true)} style={{ marginRight: 0 }}>
                <div className="ComponentName">+ Importar XML</div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/*
        O painel de avisos do módulo saiu em 28/10/2026 (portal-cotacoes/at-14, pedido do Victor:
        "unifique todos os avisos no sino geral, de todas as telas"). O desfecho de autorização de
        complemento e os demais avisos de Requisições aparecem no sino do topo, e o clique abre o
        orçamento (`components/sino-notificacoes/fontesDeAvisos.ts`).
      */}

      <div className="QuantidadeDeCarros">
        <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex' }}>
          <div className="CtaQuantidadeDeCarros">
            <div className="Frame1000003987">
              <div className="ComponentName">
                {isLoading ? (
                  'Carregando...'
                ) : isError ? (
                  'Erro ao carregar dados'
                ) : !data || data.total === 0 ? (
                  'Nenhum orçamento encontrado'
                ) : (
                  `${startCarIndex}-${endCarIndex} de ${totalCars} orçamentos`
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          width: '100%',
          paddingTop: 30,
          background: 'var(--tema-superficie, white)',
          borderRadius: 16,
          border: '1px var(--tema-linha-2, #CACACA) solid',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          display: 'flex',
          marginBottom: 20,
        }}
      >
        <div className="TabelaScroll" style={{ width: '100%' }}>
          {isLoading ? (
            <div className="cilia-loading-placeholder" style={{ padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <div className="spinner"></div>
              <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: 'var(--tema-texto-3, #6b6b6b)' }}>Carregando orçamentos...</span>
            </div>
          ) : isError ? (
            <div className="cilia-error-placeholder" style={{ padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#dc2626' }}>Erro ao carregar dados. Por favor, tente novamente.</span>
              <div className="CtaBig MouseHover" onClick={() => refetch()}>
                <div className="Frame1000003987">
                  <div className="ComponentName">Tentar Novamente</div>
                </div>
              </div>
            </div>
          ) : !data || data.data.length === 0 ? (
            <div className="cilia-empty-placeholder" style={{ padding: '60px 20px', display: 'flex', justifyContent: 'center' }}>
              <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: 'var(--tema-texto-3, #6b6b6b)' }}>Nenhum orçamento encontrado.</span>
            </div>
          ) : (
            <div
              className="TabelaContent"
              style={{
                // at-18: a Placa só tem a placa. O selo "Sem atendimento na Oficina" foi para a coluna
                // Estado, com os outros selos: com ele na Placa, a coluna tinha 250px vazios na maioria
                // das linhas. Veículo, oficina e importador saem encurtados pela célula.
                // Evento e "Importado em" saíram (pedido do Victor, 29/10/2026): os dois seguem no
                // detalhe, e o evento continua na busca e o período de importação, no filtro.
                // at-19: o espaço que sobra vai para Estado, que junta até 4 selos; com o 1fr no Veículo a
                // coluna ficava larga e vazia, e os selos saíam cortados.
                ['--table-columns' as any]: '110px 110px 110px 180px 200px minmax(300px, 1fr) 70px 140px 130px',
                width: '100%',
                minWidth: '1350px',
              }}
            >
              <div className="Tabela TabelaHeader">
                <div className="Placa">
                  <div className="TTulo">
                    <div className="Opcoes">Placa</div>
                    <div className="Opcoes">Nº Cilia</div>
                    <div className="Opcoes">Complemento</div>
                    <div className="Opcoes">Veículo</div>
                    <div className="Opcoes">Oficina</div>
                    <div className="Opcoes">Estado</div>
                    <div className="Opcoes">Itens</div>
                    <div className="Opcoes">Importado por</div>
                    <div className="Opcoes">Status Cilia</div>
                  </div>
                </div>
              </div>

              {data.data.map((orcamento) => (
                <div key={orcamento.id} className="Tabela">
                  <div className="Item">
                    <div
                      className="Data"
                      onClick={() => navigate(ORCAMENTOS_CILIA_DETAIL(orcamento.id))}
                      style={{
                        cursor: 'pointer',
                        color: '#ea5d0e',
                        fontWeight: '600',
                        textDecoration: 'underline',
                      }}
                    >
                      {orcamento.placa || '-'}
                    </div>
                  </div>
                  <div className="Item">
                    <div className="Data">{orcamento.numeroCilia} (v{orcamento.versaoCilia})</div>
                  </div>
                  <div className="Item">
                    <div
                      className="Data"
                      title={
                        orcamento.tipo === 'COMPLEMENTAR'
                          ? `Orçamento complementar (versão ${orcamento.versaoCilia} do Cilia) — foto cumulativa do inicial`
                          : undefined
                      }
                    >
                      {/* "Negado" vem do ESTADO, nunca da ausência do ordinal
                          (campo pode faltar em resposta antiga/cache) */}
                      {orcamento.tipo !== 'COMPLEMENTAR'
                        ? '—'
                        : orcamento.estado === 'AUTORIZACAO_NEGADA'
                          ? 'Negado'
                          : orcamento.complementoOrdinal
                            ? `${orcamento.complementoOrdinal}º`
                            : '—'}
                    </div>
                  </div>
                  <div className="Item">
                    <div
                      className="Data"
                      title={orcamento.dadosVeiculo?.nomeVeiculo || undefined}
                      style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                    >
                      {nomeCurtoDoVeiculo(orcamento.dadosVeiculo?.nomeVeiculo)}
                    </div>
                  </div>
                  <div className="Item">
                    {/* at-18: o nome vem do XML do Cilia. Uma linha, nome inteiro no `title`. */}
                    <div
                      className="Data"
                      title={orcamento.dadosOficina?.nome || undefined}
                      style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                    >
                      {orcamento.dadosOficina?.nome || '—'}
                    </div>
                  </div>
                  <div className="Item">
                    <div className="Data">
                      <span className={`cilia-badge ${getEstadoBadgeClass(orcamento.estado)}`}>
                        {/*
                          `_count.requisicoes` distingue os dois desfechos do `ENVIADO`.
                          Ausente (resposta em cache, backend anterior) → assume que houve
                          requisição, que é o caso comum e o rótulo de sempre.
                        */}
                        {getEstadoLabel(orcamento.estado, (orcamento._count?.requisicoes ?? 1) > 0)}
                      </span>
                      {/* Perda total (at-15): o aviso também na lista, para ninguém precisar abrir a requisição. */}
                      {orcamento.ptSituacao === 'PT_NAS_DUAS' && (
                        <span className="cilia-badge badge-vermelho" style={{ marginLeft: 6 }} title="A cotação atinge 70% da FIPE mesmo na cesta de menor valor.">
                          Perda total
                        </span>
                      )}
                      {orcamento.ptSituacao === 'PT_SO_NA_MAIOR' && (
                        <span className="cilia-badge badge-laranja" style={{ marginLeft: 6 }} title="Só a cesta de maior valor atinge 70% da FIPE.">
                          PT na cesta maior
                        </span>
                      )}
                      {!orcamento.carId && (
                        <span
                          className="cilia-badge badge-cinza"
                          style={{ marginLeft: 6 }}
                          title="Requisição iniciada sem o veículo na Oficina. É ligada sozinha quando ele der entrada."
                        >
                          Sem atendimento na Oficina
                        </span>
                      )}
                      {orcamento.teste && (
                        <span className="cilia-badge badge-laranja" style={{ marginLeft: 6 }} title='Requisição de teste: o Motivo no Nasajon começa por "TESTE - NÃO COMPRAR".'>
                          Teste
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="Item">
                    <div className="Data">{orcamento._count?.itens ?? 0}</div>
                  </div>
                  <div className="Item">
                    {/*
                      Nome desnormalizado no ato do import (migration 20260729233137). Import
                      anterior a ela fica sem nome — "—", não string vazia. `title` com o valor
                      inteiro porque a célula trunca: célula de tabela é uma linha, sempre.
                    */}
                    <div
                      className="Data"
                      title={orcamento.importadoPorNome || undefined}
                      style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                    >
                      {nomeCurtoDaPessoa(orcamento.importadoPorNome)}
                    </div>
                  </div>
                  <div className="Item">
                    <div className="Data">{orcamento.statusCilia || '—'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {data && data.total > limit && (
          <div className="PaginaO" style={{ borderTop: '1px solid var(--tema-linha, #EDEDED)', width: '100%', boxSizing: 'border-box' }}>
            <div className="Frame1000003950">
              <div 
                className="Anterior" 
                onClick={() => page > 1 && setPage((prev) => prev - 1)}
                style={{ 
                  cursor: page === 1 ? 'not-allowed' : 'pointer', 
                  color: page === 1 ? '#BEBEBE' : '#ea5d0e',
                  fontWeight: page === 1 ? '400' : '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <img src={page === 1 ? ArrowLeftGray : ArrowLeftOrange} alt="Anterior" />
                Anterior
              </div>
            </div>
            
            <div className="Group20">
              <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: 'var(--tema-texto-2, #5E5E5E)' }}>
                Página <strong>{page}</strong> de {totalPages}
              </span>
            </div>
            
            <div className="Frame1000003949">
              <div 
                className="PrXima" 
                onClick={() => page < totalPages && setPage((prev) => prev + 1)}
                style={{ 
                  cursor: page === totalPages ? 'not-allowed' : 'pointer', 
                  color: page === totalPages ? '#BEBEBE' : '#ea5d0e',
                  fontWeight: page === totalPages ? '400' : '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                Próxima
                <img src={page === totalPages ? ArrowRightGray : ArrowRightOrange} alt="Próxima" />
              </div>
            </div>
          </div>
        )}
      </div>

      {isFiltroOpen && (
        <FiltroRequisicoesModal
          filtrosAplicados={filtros}
          onAplicar={handleAplicarFiltros}
          onClose={() => setIsFiltroOpen(false)}
        />
      )}

      <ImportXmlModal
        isOpen={isImportModalOpen}
        onClose={() => {
          setIsImportModalOpen(false);
          refetch();
        }}
      />
      </>
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como funciona o módulo Cilia"
        secoes={helpSections}
      />
    </div>
  );
};

export default OrcamentoCiliaList;
